export type ModelProvider = 'openrouter' | 'digitalocean' | 'nvidia';

/**
 * One alternate host to fail this model over to when the primary provider is
 * sick (429 / 5xx / connection blip). `modelId` is only needed when the SAME
 * weights carry a different slug on the fallback host (e.g. DO 'glm-5.2' →
 * OpenRouter 'z-ai/glm-5.2'); omit it to reuse the primary id. See
 * lib/provider-router.ts for how the chain is walked and lib/provider-health.ts
 * for the circuit breaker that biases the walk.
 */
export interface ModelFallback {
  provider: ModelProvider;
  modelId?: string;
}

export interface ModelOption {
  id: string;
  name: string;
  context: number;
  /** Which API to route through. Defaults to 'openrouter' for back-compat with existing configs. */
  provider?: ModelProvider;
  /** Ordered fallback chain for provider-fallback routing (empty/absent ⇒ no failover). */
  fallbacks?: ModelFallback[];
  /** Optional raw provider input-token cost in USD per 1M tokens for upfront estimates. */
  inputCostUsdPerMillion?: number;
  /** Optional raw provider output-token cost in USD per 1M tokens for upfront estimates. */
  outputCostUsdPerMillion?: number;
  /** Optional raw provider cost in USD for one average chat turn. */
  rawCostUsdPerRequest?: number;
  /** Optional fixed estimate when a provider/model does not expose stable token pricing. */
  estimatedCredits?: number;
  estimateNotes?: string;
  creditEstimate?: {
    credits: number;
    rawCostUsd: number | null;
    providerCostMicrodollars: number | null;
    estimatedInputTokens: number;
    estimatedOutputTokens: number;
    notes: string;
  };
}

export const DEFAULT_CHAT_MODEL_ID = 'glm-5.2';
export const DEFAULT_CHAT_MODEL_PROVIDER: ModelProvider = 'digitalocean';

/**
 * Default per-step output-token ceiling for a chat turn (`chat.maxOutputTokens`
 * in app_config — this constant is only the fallback when the row is absent or
 * unparseable). Admin-overridable; NEVER hardcode a different number at a call
 * site, import this.
 *
 * Was 8_000, which was the trigger of the 2026-07-29 prod crash chain: a model
 * writing a landing page emitted `sandbox.write-file` with the whole HTML file
 * inline in the tool-call JSON, blew the 8k ceiling mid-argument, and the
 * provider returned finishReason:'length' with a truncated (unparseable)
 * tool-call payload. A file-writing tool call is legitimately tens of
 * kilobytes; 8k could not hold one.
 *
 * Why 32_000 and not more: the DigitalOcean GLM-5.2 endpoint enforces 128k
 * TOTAL (input + output) — see DEFAULT_CHAT_MODEL_OPTION.context — and
 * lib/context-manager.ts reserves the FULL output budget out of the context
 * before sizing the compaction input budget. At 32k the input budget is ~81.6k
 * tokens (vs ~102k at 8k) and resolveRequestTokenLimits does NOT clamp; at 64k
 * the input budget collapses to ~54k, which starts truncating real
 * conversations to buy output room almost no turn uses. 32k holds a ~120KB
 * inline file with headroom while keeping most of the context for history.
 */
export const DEFAULT_CHAT_MAX_OUTPUT_TOKENS = 32_000;

/**
 * Default provider-fallback chain for the default model: DigitalOcean (primary)
 * → OpenRouter serving the same GLM-5.2 weights under the 'z-ai/glm-5.2' slug.
 *
 * NVIDIA NIM is deliberately EXCLUDED: its 3-8 min cold queues make it worse
 * than failing outright for an interactive chat turn. The router MECHANISM
 * supports it (add an {provider:'nvidia'} entry via models.available config),
 * but it is not a default. When OpenRouter has balance this chain activates with
 * NO code change — it is already wired; today OpenRouter just 402s (no balance),
 * which the circuit breaker classifies as circuit-opening so the dead key is
 * skipped fast without a retry storm.
 */
export const DEFAULT_CHAT_MODEL_FALLBACKS: ModelFallback[] = [
  { provider: 'openrouter', modelId: 'z-ai/glm-5.2' },
];

export const DEFAULT_CHAT_MODEL_OPTION: ModelOption = {
  id: DEFAULT_CHAT_MODEL_ID,
  name: 'GLM 5 (DigitalOcean)',
  // DigitalOcean's serving endpoint enforces 128k total (input + output) even
  // though the GLM-5.2 architecture supports 200k+. Declaring more than the
  // endpoint accepts made the compaction budget overshoot and caused
  // "maximum context length is 128000 tokens" fatals mid-conversation.
  context: 128_000,
  provider: DEFAULT_CHAT_MODEL_PROVIDER,
  fallbacks: DEFAULT_CHAT_MODEL_FALLBACKS,
  estimatedCredits: 1,
  estimateNotes: 'Default DigitalOcean GLM-5 chat estimate.',
};

export const DEFAULT_MODEL_OPTIONS: ModelOption[] = [DEFAULT_CHAT_MODEL_OPTION];

/** Normalize a raw provider string (accepts the 'do'/'nim' aliases). */
function normalizeProvider(raw: unknown): ModelProvider | undefined {
  const p = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  if (p === 'digitalocean' || p === 'do') return 'digitalocean';
  if (p === 'nvidia' || p === 'nim') return 'nvidia';
  if (p === 'openrouter') return 'openrouter';
  return undefined;
}

/** Parse a `fallbacks` array from a config row (entries with an unknown provider
 * are dropped). Returns undefined when no valid entries exist. */
function parseFallbacks(raw: unknown): ModelFallback[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out = raw.flatMap((entry): ModelFallback[] => {
    if (!entry || typeof entry !== 'object') return [];
    const provider = normalizeProvider((entry as { provider?: unknown }).provider);
    if (!provider) return [];
    const modelId =
      typeof (entry as { modelId?: unknown }).modelId === 'string' && (entry as { modelId: string }).modelId.trim()
        ? (entry as { modelId: string }).modelId.trim()
        : undefined;
    return [{ provider, ...(modelId ? { modelId } : {}) }];
  });
  return out.length > 0 ? out : undefined;
}

export function parseModelOptions(rawValue: string | null | undefined): ModelOption[] {
  if (!rawValue) {
    return [];
  }

  try {
    const parsed = JSON.parse(rawValue);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.flatMap((item) => {
      if (!item || typeof item !== 'object') {
        return [];
      }

      const id = typeof item.id === 'string' ? item.id.trim() : '';
      const name = typeof item.name === 'string' ? item.name.trim() : '';
      const context = typeof item.context === 'number' ? item.context : 0;
      const inputCostUsdPerMillion =
        typeof item.inputCostUsdPerMillion === 'number' ? item.inputCostUsdPerMillion : undefined;
      const outputCostUsdPerMillion =
        typeof item.outputCostUsdPerMillion === 'number' ? item.outputCostUsdPerMillion : undefined;
      const rawCostUsdPerRequest =
        typeof item.rawCostUsdPerRequest === 'number' ? item.rawCostUsdPerRequest : undefined;
      const estimatedCredits =
        typeof item.estimatedCredits === 'number' ? item.estimatedCredits : undefined;
      const estimateNotes =
        typeof item.estimateNotes === 'string' && item.estimateNotes.trim()
          ? item.estimateNotes.trim()
          : undefined;
      const provider = normalizeProvider(item.provider);
      const fallbacks = parseFallbacks(item.fallbacks);

      if (!id || !name) {
        return [];
      }

      return [{
        id,
        name,
        context,
        ...(provider ? { provider } : {}),
        ...(fallbacks ? { fallbacks } : {}),
        ...(inputCostUsdPerMillion !== undefined ? { inputCostUsdPerMillion } : {}),
        ...(outputCostUsdPerMillion !== undefined ? { outputCostUsdPerMillion } : {}),
        ...(rawCostUsdPerRequest !== undefined ? { rawCostUsdPerRequest } : {}),
        ...(estimatedCredits !== undefined ? { estimatedCredits } : {}),
        ...(estimateNotes ? { estimateNotes } : {}),
      }];
    });
  } catch {
    return [];
  }
}

export function mergeDefaultModelOptions(models: ModelOption[]): ModelOption[] {
  const configuredById = new Map(models.map((model) => [model.id, model]));

  const defaults = DEFAULT_MODEL_OPTIONS.map((defaultModel) => {
    const configured = configuredById.get(defaultModel.id);
    if (!configured) return defaultModel;

    configuredById.delete(defaultModel.id);
    return {
      ...defaultModel,
      ...configured,
      provider: configured.provider ?? defaultModel.provider,
      context: configured.context || defaultModel.context,
    };
  });

  return [...defaults, ...configuredById.values()];
}

export function getModelOptionById(models: ModelOption[], id: string | null | undefined) {
  if (!id) {
    return undefined;
  }

  return mergeDefaultModelOptions(models).find((model) => model.id === id);
}
